/**
 * AssessmentsPanel - Main component for managing patient assessments
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chip } from "../../components/ui";
import ConfirmDialog from "../../components/ConfirmDialog";
import { styles, btnPlain } from "./styles";
import { useAssessmentsApi } from "./useAssessmentsApi";
import { useInstruments } from "./useInstruments";
import { usePatientContact } from "./usePatientContact";
import { AssessmentTile } from "./AssessmentTile";
import { AssessmentDrawer } from "./AssessmentDrawer";
import { SendQuestionnaireDialog } from "./SendQuestionnaireDialog";
import type { SendSelection } from "./SendQuestionnaireDialog";
import { QuestionnaireShelf } from "./QuestionnaireShelf";
import { QuestionnaireReview } from "./QuestionnaireReview";
import { useQuestionnaireFiles } from "./useQuestionnaireFiles";
import type { CustomInstrument, QuestionnaireTemplate } from "./useQuestionnaireFiles";
import type { SendQuestionnaireParams, UploadedAssessment, BusyState } from "./types";

type Props = {
  patientId: string;
  sessionId?: string;
};

const AssessmentsPanel: React.FC<Props> = ({ patientId, sessionId }) => {
  const { t, i18n } = useTranslation();
  const api = useAssessmentsApi(patientId, sessionId);
  const { list: listTemplates } = useQuestionnaireFiles();
  const { instruments, byId, error: instrumentsError } = useInstruments();
  const { contact: patientContact, loading: patientContactLoading } = usePatientContact(patientId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uiLang: "en" | "hu" = i18n.language === "hu" ? "hu" : "en";

  const [uploading, setUploading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [items, setItems] = useState<UploadedAssessment[]>([]);
  const [busyIds, setBusyIds] = useState<BusyState>({});
  const [sendOpen, setSendOpen] = useState(false);
  const [sendPreselect, setSendPreselect] = useState<SendSelection | null>(null);

  // Custom-questionnaire template list (Task 4): fetched ONCE here and
  // shared by BOTH QuestionnaireShelf and SendQuestionnaireDialog — a
  // template uploaded/reviewed in the shelf is immediately visible in the
  // send picker with no separate fetch or staleness window. This replaces
  // Task 3's stopgaps: the review screen no longer re-fetches the list on
  // its own to find the row it needs, and the shelf no longer gets
  // force-remounted (`key={shelfRefreshKey}`) to pick up a save — both now
  // just read/refresh this single shared `templates` state.
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const fail = useCallback((where: string, e: any, fallbackMsg: string) => {
    const msg = e?.message ? String(e.message) : fallbackMsg;
    console.error(`[AssessmentsPanel] ${where}:`, e);
    setError(msg);
  }, []);

  const refreshTemplates = useCallback(async (signal?: AbortSignal) => {
    setTemplatesLoading(true);
    try {
      const rows = await listTemplates(signal);
      setTemplates(rows);
    } catch (e: any) {
      if (signal?.aborted) return;
      fail("refreshTemplates", e, t("qshelf.loadFailed"));
    } finally {
      if (!signal?.aborted) setTemplatesLoading(false);
    }
  }, [listTemplates, fail, t]);

  useEffect(() => {
    const ac = new AbortController();
    void refreshTemplates(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTemplates]);

  // Custom-questionnaire review screen (Task 3). The parsed instrument to
  // start from is read directly out of the shared `templates` state above
  // (no per-open re-fetch needed now that the list is lifted).
  const [reviewFileId, setReviewFileId] = useState<string | null>(null);
  const [reviewInstrument, setReviewInstrument] = useState<CustomInstrument | null>(null);

  // Drawer editor state
  const [openId, setOpenId] = useState<string | null>(null);
  const [editParserText, setEditParserText] = useState<string>("");
  const [deleteTarget, setDeleteTarget] = useState<UploadedAssessment | null>(null);
  const [resultText, setResultText] = useState<string>("");

  const openItem = useMemo(() => items.find((x) => x.id === openId) || null, [items, openId]);

  useEffect(() => {
    if (!patientId || !patientId.trim()) {
      setError("AssessmentsPanel misconfigured: missing required patientId prop.");
    }
  }, [patientId]);

  // Load list on mount
  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setNotice(null);
        setLoadingList(true);
        const list = await api.loadList(ac.signal);
        setItems(list);
      } catch (e: any) {
        if (ac.signal.aborted) return;
        fail("initial loadList", e, t("assessments.loadFailed"));
      } finally {
        setLoadingList(false);
      }
    })();

    return () => ac.abort();
  }, [api.loadList]);

  const handlePickFile = () => {
    if (!patientId?.trim()) {
      setError("Cannot upload: missing required patientId prop.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!patientId?.trim()) return;

    setError(null);
    setNotice(null);
    setUploading(true);

    try {
      await api.uploadFile(file);
      setNotice(t("assessments.noticeUploaded"));
      const list = await api.loadList();
      setItems(list);
    } catch (e: any) {
      fail("upload", e, t("assessments.uploadFailed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openEditor = async (id: string) => {
    setError(null);
    setNotice(null);
    setOpenId(id);

    // Manually-completed rows never carry digital responses — the drawer
    // renders "no digital responses" straight off the list-level data, so
    // skip the getAssessment round-trip entirely for them.
    const current = items.find((x) => x.id === id);
    if (current?.kind === "questionnaire" && current.completedManually) {
      setEditParserText("");
      setResultText("");
      return;
    }

    try {
      const fresh = await api.getAssessment(id);
      setItems((prev) => prev.map((x) => (x.id === id ? fresh : x)));
      setEditParserText(fresh.parserText || "");
      setResultText(fresh.parseOutputText || "");
    } catch (e: any) {
      fail("openEditor", e, t("assessments.openEditorFailed"));
    }
  };

  const closeEditor = () => {
    setOpenId(null);
    setEditParserText("");
    setResultText("");
  };

  const onSave = async () => {
    if (!openItem) return;
    if (busyIds[openItem.id] || openItem.parseStatus === "pending") return;

    setError(null);
    setNotice(null);
    setBusyIds((m) => ({ ...m, [openItem.id]: "save" }));

    try {
      await api.saveParserText(openItem.id, editParserText);
      const fresh = await api.fetchOne(openItem.id);
      setItems((prev) => prev.map((x) => (x.id === openItem.id ? fresh : x)));
      setNotice(t("assessments.noticeSaved"));
    } catch (e: any) {
      fail("onSave", e, t("assessments.saveFailed"));
    } finally {
      setBusyIds((m) => {
        const { [openItem.id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const onRun = async () => {
    if (!openItem) return;
    if (busyIds[openItem.id] || openItem.parseStatus === "pending") return;

    setError(null);
    setNotice(null);
    setBusyIds((m) => ({ ...m, [openItem.id]: "parse" }));
    setItems((prev) => prev.map((x) => (x.id === openItem.id ? { ...x, parseStatus: "pending" as const } : x)));

    try {
      const fresh0 = await api.fetchOne(openItem.id);
      setItems((prev) => prev.map((x) => (x.id === openItem.id ? fresh0 : x)));

      await api.startParse(fresh0, editParserText?.trim() ? editParserText : undefined);

      const fresh = await api.waitForParseDone(openItem.id, (f) => {
        setItems((prev) => prev.map((x) => (x.id === openItem.id ? f : x)));
      });

      setResultText(fresh.parseOutputText || "");
      if (fresh.parseStatus === "ok") setNotice(t("assessments.noticeParsed"));
      if (fresh.parseStatus === "error") setError(fresh.parseError || t("assessments.parseFailed"));
    } catch (e: any) {
      fail("onRun", e, t("assessments.parseFailed"));
      try {
        const fresh = await api.fetchOne(openItem.id);
        setItems((prev) => prev.map((x) => (x.id === openItem.id ? fresh : x)));
        setResultText(fresh.parseOutputText || "");
      } catch (e2: any) {
        fail("onRun(refresh)", e2, t("assessments.parseRefreshFailed"));
      }
    } finally {
      setBusyIds((m) => {
        const { [openItem.id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const a = deleteTarget;
    // Every questionnaire row that reaches this dialog is a "sent" +
    // email-channel cancel-send (AssessmentTile only renders the trash
    // button under that gate) — route it through cancelSend for a clear
    // call site, even though it's the same guarded DELETE as deleteOne.
    const isCancelSend = a.kind === "questionnaire";
    setDeleteTarget(null);

    setError(null);
    setNotice(null);
    setBusyIds((m) => ({ ...m, [a.id]: "delete" }));

    const snapshot = items;
    setItems((prev) => prev.filter((x) => x.id !== a.id));

    try {
      if (isCancelSend) {
        await api.cancelSend(a.id);
      } else {
        await api.deleteOne(a.id);
      }
      if (openId === a.id) closeEditor();
      setNotice(t(isCancelSend ? "assessments.noticeCancelled" : "assessments.noticeDeleted"));
      const list = await api.loadList();
      setItems(list);
    } catch (e: any) {
      setItems(snapshot);
      fail("confirmDelete", e, t(isCancelSend ? "assessments.cancelSendFailed" : "assessments.deleteFailed"));
    } finally {
      setBusyIds((m) => {
        const { [a.id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const onMarkCompleted = async (target: UploadedAssessment) => {
    if (busyIds[target.id]) return;

    setError(null);
    setNotice(null);
    setBusyIds((m) => ({ ...m, [target.id]: "markCompleted" }));

    const snapshot = items;
    setItems((prev) =>
      prev.map((x) => (x.id === target.id ? { ...x, status: "completed" as const, completedManually: true } : x))
    );

    try {
      const patch = await api.markCompleted(target.id);
      setItems((prev) =>
        prev.map((x) =>
          x.id === target.id
            ? { ...x, status: "completed" as const, completedManually: true, completedAt: patch.completedAt }
            : x
        )
      );
      setNotice(t("assessments.noticeMarkedCompleted"));
    } catch (e: any) {
      setItems(snapshot);
      fail("markCompleted", e, t("assessments.markCompletedFailed"));
    } finally {
      setBusyIds((m) => {
        const { [target.id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const filesCountChip = (
    <Chip
      text={
        loadingList
          ? t("common.loading")
          : items.length
          ? t("assessments.filesCount", { count: items.length })
          : t("assessments.noUploadsChip")
      }
      tone={items.length ? "ok" : "muted"}
    />
  );

  const onSendQuestionnaire = async (params: SendQuestionnaireParams) => {
    const result = await api.sendQuestionnaire(params);
    const list = await api.loadList();
    setItems(list);
    return result;
  };

  // Every questionnaire row reaching this dialog is a "sent" + email-channel
  // cancel-send (see confirmDelete) — its title/message/label read as a
  // cancellation, distinct from the destructive "Delete" of an upload.
  const isCancelSendTarget = deleteTarget?.kind === "questionnaire";
  const deleteMessage = isCancelSendTarget
    ? t("assessments.cancelSendConfirm", {
        name: deleteTarget?.instrumentSnapshotName ?? byId(deleteTarget?.instrumentId)?.name[uiLang] ?? deleteTarget?.instrumentId,
      })
    : t("assessments.deleteConfirm", { name: deleteTarget?.filename });

  // The parsed instrument to review is read straight out of the shared
  // `templates` state (Task 4 list lift) — no per-open re-fetch needed.
  const onReviewQuestionnaireFile = (fileId: string) => {
    setError(null);
    const row = templates.find((tpl) => tpl.fileId === fileId);
    if (!row || !row.parsedInstrument) {
      fail("onReviewQuestionnaireFile", new Error(`template not found: ${fileId}`), t("assessments.reviewTemplateNotFound"));
      return;
    }
    setReviewInstrument(row.parsedInstrument);
    setReviewFileId(fileId);
  };

  const closeReview = () => {
    setReviewFileId(null);
    setReviewInstrument(null);
  };

  const onReviewSaved = () => {
    closeReview();
    void refreshTemplates(); // status flips needs_review -> ready in the shared list
  };

  const openSendDialog = () => {
    setSendPreselect(null); // generic "Send questionnaire" button: no preselection
    setSendOpen(true);
  };

  const onSendQuestionnaireTemplate = (template: QuestionnaireTemplate) => {
    setSendPreselect({ kind: "custom", id: template.fileId });
    setSendOpen(true);
  };

  const closeSendDialog = () => {
    setSendOpen(false);
    setSendPreselect(null);
  };

  return (
    <div style={styles.grid}>
      <style>{`@keyframes ap-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Custom questionnaire files: upload + parse-status shelf */}
      <div style={styles.card}>
        <QuestionnaireShelf
          items={templates}
          loading={templatesLoading}
          onRefresh={refreshTemplates}
          onReview={onReviewQuestionnaireFile}
          onSend={onSendQuestionnaireTemplate}
        />
      </div>

      {reviewFileId && reviewInstrument ? (
        <QuestionnaireReview
          fileId={reviewFileId}
          instrument={reviewInstrument}
          onSaved={onReviewSaved}
          onCancel={closeReview}
        />
      ) : null}

      {/* Upload card */}
      <div style={styles.card}>
        <div style={styles.cardHead}>
          <div style={{ fontWeight: 900, color: "var(--color-title)" }}>{t("assessments.upload")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnPlain} onClick={openSendDialog}>
              {t("assessments.sendQuestionnaire")}
            </button>
            <button style={btnPlain} onClick={handlePickFile} disabled={uploading}>
              {uploading ? t("assessments.uploading") : t("assessments.chooseFile")}
            </button>
          </div>
        </div>

        {error ? (
          <div style={{ fontSize: 12, color: "var(--color-danger)" }}>
            {t("common.error")}: {error}
          </div>
        ) : null}
        {instrumentsError ? (
          <div style={{ fontSize: 12, color: "var(--color-danger)" }}>{instrumentsError}</div>
        ) : null}
        {notice ? <div style={{ fontSize: 12, color: "var(--color-success)" }}>{notice}</div> : null}

        <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileChange} />
      </div>

      {/* Uploaded list */}
      <div style={styles.card}>
        <div style={styles.cardHead}>
          <div style={{ fontWeight: 900, color: "var(--color-title)" }}>{t("assessments.uploaded")}</div>
          {filesCountChip}
        </div>

        {!items.length && !loadingList ? (
          <div style={{ fontSize: 13, color: "var(--color-muted)" }}>{t("assessments.noUploads")}</div>
        ) : null}

        {items.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {items.map((it) => (
              <AssessmentTile
                key={it.id}
                item={it}
                busy={busyIds[it.id]}
                onOpen={openEditor}
                onDelete={setDeleteTarget}
                onMarkCompleted={onMarkCompleted}
                instrument={byId(it.instrumentId)}
                uiLang={uiLang}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Drawer editor */}
      {openItem ? (
        <AssessmentDrawer
          item={openItem}
          busy={busyIds[openItem.id]}
          editParserText={editParserText}
          setEditParserText={setEditParserText}
          resultText={resultText}
          onRun={onRun}
          onSave={onSave}
          onClose={closeEditor}
          instrument={byId(openItem.instrumentId)}
          uiLang={uiLang}
        />
      ) : null}

      <SendQuestionnaireDialog
        open={sendOpen}
        instruments={instruments ?? []}
        templates={templates}
        defaultLang={uiLang}
        patientEmail={patientContact?.email}
        patientClientStatus={patientContact?.clientStatus}
        patientContactLoading={patientContactLoading}
        initialSelection={sendPreselect}
        onSend={onSendQuestionnaire}
        onClose={closeSendDialog}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={isCancelSendTarget ? t("assessments.cancelSendTitle") : t("assessments.deleteTitle")}
        message={deleteMessage}
        confirmLabel={isCancelSendTarget ? t("assessments.cancelSendConfirmLabel") : t("common.delete")}
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AssessmentsPanel;
